import React, { useState } from 'react';
import { Box, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { SkuItem, VariantStockSource, ProductVariant } from 'common/modules/inventory';
import { getProductVariantName, getVariantSkuId } from 'common/modules/products/variants';
import {
	skuItemsToOptions,
	SkuOption,
	getSkuAllocationsByLocation,
	getSkuReservedAmountByLocation,
	getSkuInStockAmountByLocation,
} from 'views/Inventory/utils';
import { Autocomplete } from '@mui/material';
import { useSelector } from 'react-redux';
import * as UserSelectors from 'selectors/UserSelectors';
import { useTranslation } from 'services/localization';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import ColorCodedTypography from 'components/ColorCodedTypography';

interface Props {
	productName: string;
	variant: ProductVariant;
	onStockSourcesChange: (stockSource: VariantStockSource | null) => void;
	skuItems: SkuItem[];
	styling?: {
		inputSize?: 'small' | 'medium';
	};
}

const ConnectVariantRow = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);
	const { productName, variant, skuItems, styling = { inputSize: 'medium' } } = props;
	const { inputSize = 'medium' } = styling;
	const [searchValue, setSearchValue] = useState<string>('');

	const handleSkuChange = (skuOption: SkuOption | null) => {
		const newStockSource: VariantStockSource | null =
			skuOption === null
				? null
				: {
						0: [
							{
								skuId: skuOption.id,
								type: skuOption.type === 'single' ? 'item' : 'bulk',
								units: 1,
							},
						],
				  };
		props.onStockSourcesChange(newStockSource);
	};

	const variantName = getProductVariantName(productName, Object.values(variant.values), 'def');
	const skuOptions = skuItemsToOptions(skuItems);
	const selectedSkuId = getVariantSkuId(variant);
	const selectedSku = skuItems.find((sku) => sku.id === selectedSkuId);
	const selectedSkuOption = skuOptions.find((option) => option.id === selectedSkuId);

	const selectedSkuStockQuantity = getSkuInStockAmountByLocation(selectedSku, activeLocationId);
	const selectedSkuRentalQuantity = getSkuAllocationsByLocation(
		selectedSku,
		activeLocationId,
		'rental',
	);
	const selectedSkuSellQuantity = getSkuAllocationsByLocation(
		selectedSku,
		activeLocationId,
		'sales',
	);

	const selectedSkuReservedQuantity = getSkuReservedAmountByLocation(selectedSku, activeLocationId);

	return (
		<Box display={isMobile ? 'block' : 'flex'} alignItems="center" justifyContent="space-between">
			<Typography variant="body1">{variantName}</Typography>
			<Box minWidth={300} width="100%" maxWidth={400} mt={isMobile ? 1 : 0}>
				<Autocomplete
					disablePortal
					value={selectedSkuOption ?? null}
					disabled={isEditingDisabled}
					onChange={(_: any, option) => handleSkuChange(option)}
					inputValue={searchValue}
					getOptionLabel={(opt) => opt.label}
					onInputChange={(_, newInputValue) => setSearchValue(newInputValue)}
					options={skuOptions}
					renderInput={(params) => (
						<TextField
							{...params}
							label={t('inventory.chooseSku', 'Choose SKU')}
							variant="outlined"
							placeholder={`${t('common:actions.search')}...`}
						/>
					)}
					classes={{
						option: classes.listOption,
					}}
					size={inputSize}
				/>
				{!!selectedSku && (
					<Stack direction="row" alignItems="flex-start" flexWrap="wrap">
						<ColorCodedTypography
							variant="body2"
							align="left"
							value={selectedSkuStockQuantity}
							prefix={`${t('product.inStock', 'In stock')}: `}
							sx={{
								mt: 0.5,
								mr: 2,
							}}
						/>
						<ColorCodedTypography
							variant="body2"
							align="left"
							value={selectedSkuRentalQuantity}
							prefix={`${t('inventory.forRent', 'For rent')}: `}
							sx={{
								mt: 0.5,
								mr: 2,
							}}
						/>
						<ColorCodedTypography
							variant="body2"
							align="left"
							value={selectedSkuSellQuantity - selectedSkuReservedQuantity}
							prefix={`${t('inventory.forSale', 'For sale')}: `}
							sx={{
								mt: 0.5,
								mr: 2,
							}}
						/>
						<ColorCodedTypography
							variant="body2"
							align="left"
							value={selectedSkuReservedQuantity}
							prefix={`${t('inventory.committed', 'Committed')}: `}
							tooltip={t(
								'inventory.committedDescription',
								'These items have been assigned to sales orders which have not yet been fulfilled',
							)}
							hideIfZero
							sx={{
								mt: 0.5,
								mr: 2,
							}}
						/>
					</Stack>
				)}
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listOption: {
			padding: theme.spacing(1.5, 1),
		},
	}),
);

export default ConnectVariantRow;
